<template>
    <ValidationProvider
        ref="validatorRef"
        :name="name"
        :rules="rules"
        :value="validationValue"
        v-slot="{ errors }"
        mode="eager"
    >
        <div
            class="tw:relative tw:flex tw:flex-col tw:items-center tw:justify-center tw:p-2 tw:md:p-4 tw:border-2 tw:border-dashed tw:rounded-lg tw:cursor-pointer tw:transition-colors tw:duration-200"
            :class="[dropzoneClasses, errors[0] ? 'tw:border-red-500' : '']"
            :data-validation-error="errors[0] ? '' : undefined"
            @dragover.prevent="onDragOver"
            @dragleave.prevent="onDragLeave"
            @drop.prevent="onDrop"
            @click="triggerFileInput"
        >
            <!-- Idle state. El padding lo aporta el contenedor (ver arriba),
                 así que cada estado no declara el suyo. -->
            <template v-if="!isUploading && !uploadedFile && !errorMessage && !hasRemotePreview">
                <div class="tw:flex tw:flex-col tw:items-center tw:justify-center">
                    <svg class="tw:w-10 tw:h-10 tw:mb-3 tw:text-slate-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p class="tw:text-sm tw:text-slate-500">
                        <span class="tw:font-semibold tw:text-blue-600">Click to upload</span> or drag and drop
                    </p>
                    <p class="tw:text-xs tw:text-slate-400 tw:mt-1">
                        {{ acceptedTypesLabel }} (max {{ maxFilesize }}MB)
                    </p>
                </div>
            </template>

            <!-- Hovering state -->
            <div v-if="isDragging && !isUploading" class="tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center tw:bg-blue-50 tw:rounded-lg tw:z-10">
                <p class="tw:text-sm tw:font-semibold tw:text-blue-600">Drop files here</p>
            </div>

            <!-- Uploading state. -->
            <div v-if="isUploading" class="tw:w-full">
                <p class="tw:text-sm tw:text-slate-600 tw:mb-2">Uploading...</p>
                <div class="tw:w-full tw:bg-gray-200 tw:rounded-full tw:h-2.5">
                    <div
                        class="tw:bg-blue-600 tw:h-2.5 tw:rounded-full tw:transition-all tw:duration-300"
                        :style="{ width: uploadProgress + '%' }"
                    ></div>
                </div>
                <p class="tw:text-xs tw:text-slate-400 tw:mt-1">{{ Math.round(uploadProgress) }}%</p>
            </div>

            <!-- Success state (covers both create-mode upload and edit-mode
                 existing image): render whenever we have something to show
                 (local File or remote preview URL). The hover overlay reads
                 displayName / displaySize, which fall back to derived
                 values from the preview URL when there's no local file. -->
            <div v-if="(uploadedFile || hasRemotePreview) && !isUploading" class="tw:relative tw:group tw:inline-block tw:max-w-full">
                <!-- Thumbnail (image) or icon block (non-image / unsupported MIME) -->
                <img
                    v-if="isImage && displayUrl"
                    :src="displayUrl"
                    :alt="displayName"
                    class="tw:block tw:w-full tw:max-w-lg tw:h-auto tw:rounded tw:border tw:border-slate-200"
                />
                <div
                    v-else
                    class="tw:w-32 tw:h-32 tw:flex tw:items-center tw:justify-center tw:bg-slate-50 tw:rounded tw:border tw:border-slate-200"
                >
                    <svg
                        class="tw:w-10 tw:h-10 tw:text-green-500"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </div>

                <!-- Hover overlay (desktop only): file info centered at the top,
                     with a dark scrim. On touch devices there is no hover, so
                     the overlay never shows — the delete button below is
                     rendered OUTSIDE it and is always visible there.
                     `pointer-events-none` on the overlay so the click that
                     opens the file picker (triggered on the outer dropzone
                     div) still fires when the user clicks empty space. -->
                <div class="tw:hidden tw:md:block tw:absolute tw:inset-0 tw:rounded tw:bg-black/60 tw:opacity-0 tw:group-hover:opacity-100 tw:transition-opacity tw:pointer-events-none">
                    <div class="tw:absolute tw:top-2 tw:left-0 tw:right-0 tw:px-3 tw:text-white tw:text-center">
                        <p
                            class="tw:text-xs tw:font-medium tw:truncate"
                            :title="displayName"
                        >{{ displayName }}</p>
                        <p v-if="displaySize" class="tw:text-[10px] tw:text-white/70">{{ displaySize }}</p>
                    </div>
                </div>

                <!-- Delete button. Always visible on mobile (no hover there);
                     on desktop it still only appears on group hover, matching
                     the overlay behaviour. `@click.stop` keeps the tap from
                     re-opening the file picker through the outer div. -->
                <button
                    type="button"
                    class="tw:absolute tw:bottom-2 tw:right-2 tw:z-10 tw:pointer-events-auto tw:text-red-600 tw:focus:outline-none tw:bg-black/40 tw:hover:bg-red-200 tw:rounded-full tw:p-2 tw:md:pointer-events-none tw:md:opacity-0 tw:md:transition-opacity tw:md:group-hover:pointer-events-auto tw:md:group-hover:opacity-100"
                    title="Eliminar"
                    @click.stop="onDeleteClick"
                >
                    <svg
                        class="tw:w-6 tw:h-6"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                </button>
            </div>

            <!-- Error state (internal upload-time errors: wrong type, too large, network). -->
            <div v-if="errorMessage && !isUploading" class="tw:text-center">
                <svg class="tw:w-8 tw:h-8 tw:mx-auto tw:mb-2 tw:text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p class="tw:text-sm tw:text-red-600">{{ errorMessage }}</p>
                <button
                    type="button"
                    class="tw:mt-2 tw:text-sm tw:text-blue-600 tw:hover:underline tw:focus:outline-none"
                    @click.stop="resetDropzone"
                >
                    Try again
                </button>
            </div>

            <!-- Hidden file input -->
            <input
                ref="fileInput"
                type="file"
                :accept="props.acceptedFiles"
                :multiple="uploadMultiple"
                class="tw:hidden"
                @change="onFileSelect"
            />
        </div>
        <span
            v-if="errors[0]"
            class="tw:block tw:mt-1 tw:text-xs tw:text-red-600 tw:font-medium"
        >{{ errors[0] }}</span>
        <span
            v-if="props.apiValidationError && !errors[0]"
            class="tw:block tw:mt-1 tw:text-xs tw:text-red-600 tw:font-medium"
        >{{ Array.isArray(props.apiValidationError) ? props.apiValidationError.join(', ') : props.apiValidationError }}</span>
    </ValidationProvider>
</template>

<script setup>
    import { ref, computed, watch, onMounted, onUnmounted } from 'vue';

    const props = defineProps({
        url: { type: String, required: true },
        acceptedFiles: {
            type: String,
            default: 'image/jpeg,image/png,image/gif,image/bmp,image/tiff'
        },
        maxFilesize: { type: Number, default: 20 },
        maxFiles: { type: Number, default: 1 },
        disabled: { type: Boolean, default: false },
        height: { type: Number, default: null },
        width: { type: Number, default: null },
        uploadMultiple: { type: Boolean, default: false },
        // Multi-upload: when `uploadMultiple` is true and the parent binds
        // `v-model:uploadedFiles`, this array carries every successfully
        // uploaded file (with its server response) so the parent can render
        // a per-file list and remove individual files.
        uploadedFiles: { type: Array, default: () => [] },
        // URL of an image that already exists on the server (edit mode).
        // Used as fallback for the thumbnail preview and the validation
        // `required` rule when the user has not uploaded a new file in this
        // session. Pass `localData.image` (or whatever your backend returns)
        // to make the component display the existing image on edit.
        preview: { type: String, default: null },
        // === Validation (vee-validate) — opt-in. Pass `name` + `rules` to
        // participate in a parent <ValidationObserver>; the internal
        // <ValidationProvider> renders the red border + error message below
        // the dropzone. Slot has no native form field, so we explicitly call
        // `refreshValidation()` on upload/remove to sync the provider's
        // internal value (see `vee-validate v3.3.6 quirk` note in script). ===
        name: { type: String, default: null },
        rules: { type: [String, Object], default: '' },
        apiValidationError: { type: [String, Array], default: null }
    });

    const emit = defineEmits(['success', 'error', 'removed', 'update:uploadedFiles', 'remove-file']);

    const fileInput = ref(null);
    const isDragging = ref(false);
    const isUploading = ref(false);
    const uploadProgress = ref(0);
    const uploadedFile = ref(null);
    const errorMessage = ref('');

    // Internal mirror of the prop so we can mutate and emit without
    // violating v-model immutability expectations.
    const uploadedFilesLocal = ref([...(props.uploadedFiles || [])]);

    watch(() => props.uploadedFiles, value => {
        uploadedFilesLocal.value = [...(value || [])];
    }, { deep: true });

    // === Image preview ===
    // `URL.createObjectURL(file)` returns a blob: URL we can bind to <img src>.
    // The URL is created when `uploadedFile` changes and revoked on every
    // transition (new upload, remove, unmount) so the browser can free
    // the underlying blob — otherwise each upload leaks until page reload.
    const previewUrl = ref(null);

    const isImage = computed(() => {
        const file = uploadedFile.value;
        if (file && file.type && file.type.startsWith('image/')) return true;
        // Existing image on the server (edit mode) — accept any URL with
        // an image-like extension as an image preview.
        if (props.preview) {
            return /\.(png|jpe?g|gif|bmp|tiff?|webp|svg)(\?|$)/i.test(props.preview);
        }
        return false;
    });

    // Resolve the URL the <img> should bind to. Local blob always wins
    // (it represents what the user just uploaded this session); fall back
    // to the prop URL when there's no local file (edit / display-only).
    // Used for both the `v-if` (so the element actually renders) and the
    // `:src` binding.
    const displayUrl = computed(() => {
        if (previewUrl.value) return previewUrl.value;
        return props.preview || null;
    });

    // True when there's a remote image to show but no local upload yet.
    // This is the edit-mode path: server-side image exists (preview prop)
    // and the user hasn't replaced it in this session.
    const hasRemotePreview = computed(() => !!props.preview && !previewUrl.value);

    // Display values used by the hover overlay. In create mode the data
    // comes from `uploadedFile` (File object); in edit mode without a
    // local upload we derive a label from the preview URL.
    const displayName = computed(() => {
        if (uploadedFile.value?.name) return uploadedFile.value.name;
        if (props.preview) {
            try {
                const url = new URL(props.preview, window.location.origin);
                const segments = url.pathname.split('/').filter(Boolean);
                return segments[segments.length - 1] || 'imagen';
            } catch {
                return 'imagen';
            }
        }
        return '';
    });

    const displaySize = computed(() => {
        if (!uploadedFile.value) return null;
        return `${(uploadedFile.value.size / 1024 / 1024).toFixed(2)} MB`;
    });

    // Unified delete handler. In create mode we clear the local file and
    // notify the parent (which clears localData.imagen). In edit mode
    // there's no local file — we just notify the parent so it clears the
    // bound image data on submit.
    function onDeleteClick() {
        if (uploadedFile.value) {
            removeFile();
        } else {
            emit('removed');
        }
    }

    watch(uploadedFile, file => {
        if (previewUrl.value) {
            URL.revokeObjectURL(previewUrl.value);
            previewUrl.value = null;
        }
        previewUrl.value = file && file.type?.startsWith('image/') ? URL.createObjectURL(file) : null;
    });

    onUnmounted(() => {
        if (previewUrl.value) {
            URL.revokeObjectURL(previewUrl.value);
            previewUrl.value = null;
        }
    });

    // === Validation wiring ===
    // Vee-validate v3.3.6 quirk: ValidationProvider only auto-syncs its
    // internal `value` when a native form field inside its slot fires
    // `input`/`change`. Our slot has no input, so we expose a ref and
    // re-validate explicitly. Two triggers:
    //   1. `watch(validationValue, refreshValidation)` — fires whenever
    //      the provider's bound value changes (new upload, new preview
    //      prop from parent, file deleted). Watching `validationValue`
    //      covers both create-mode (`uploadedFile` change) and edit-mode
    //      (`props.preview` arrives asynchronously from the API).
    //   2. `refreshValidation()` called synchronously INSIDE `removeFile()`
    //      and `xhr.onload` BEFORE the `emit(...)` so the parent listener
    //      and the observer submit gate both see the fresh provider value
    //      in the same tick.
    const validatorRef = ref(null);

    const hasFile = computed(() =>
        !!(uploadedFile.value || uploadedFilesLocal.value.length > 0)
    );

    // Value fed to the ValidationProvider. We pass the actual File/array
    // (or null/[]) instead of a boolean because vee-validate's `required`
    // rule treats `false` as VALID — only null/undefined/empty-string count
    // as "empty". With a boolean, the rule never fails on the delete path.
    // Value fed to the ValidationProvider. We pass the actual File/array
    // (or null/[]) instead of a boolean because vee-validate's `required`
    // rule treats `false` as VALID — only null/undefined/empty-string count
    // as "empty". With a boolean, the rule never fails on the delete path.
    // In edit mode (`preview` prop set, no local file), we use the URL
    // string as a truthy non-empty value so `required` still passes.
    const validationValue = computed(() => {
        if (props.uploadMultiple) {
            return uploadedFilesLocal.value.length > 0 ? uploadedFilesLocal.value : null;
        }
        return uploadedFile.value || props.preview || null;
    });

    function refreshValidation() {
        const v = validatorRef.value;
        if (v && typeof v.validate === 'function') {
            v.validate(validationValue.value);
        }
    }

    // Watch `validationValue` (not `hasFile`) so the edit-mode case works:
    // when the API returns `imagen`, the parent passes it as `preview`,
    // `validationValue` flips from null to the URL, this watcher fires,
    // and the provider's internal `this.value` gets synced. With
    // `hasFile` only, this never fired in edit mode (uploadedFile stays
    // null, so hasFile never goes true → false).
    watch(validationValue, refreshValidation);

    // In edit mode `validationValue` is already the URL on the very first
    // render (no transition from null), so the watcher above never fires.
    // `refreshValidation()` needs to run once on mount AFTER the template
    // ref (`validatorRef`) has been populated — setup-time is too early
    // (validatorRef.value is still null then). `onMounted` runs after the
    // initial render commits, so the ref is populated and `validate()` can
    // actually sync `this.value` inside the provider.
    onMounted(refreshValidation);

    const acceptedTypesLabel = computed(() => {
        if (!props.acceptedFiles) return 'All files';
        const types = props.acceptedFiles.split(',');
        return types.map(t => t.split('/')[1]?.toUpperCase() || t).join(', ');
    });

    const dropzoneClasses = computed(() => ({
        'tw:border-blue-300 tw:bg-blue-50': isDragging.value && !props.disabled,
        'tw:border-green-300 tw:bg-green-50': uploadedFile.value && !isUploading.value,
        'tw:border-red-300 tw:bg-red-50': errorMessage.value && !isUploading.value,
        'tw:border-gray-300 tw:bg-white': !isDragging.value && !uploadedFile.value && !errorMessage.value,
        'tw:opacity-50 tw:cursor-not-allowed': props.disabled,
        'tw:hover:border-blue-400': !props.disabled && !isUploading.value && !uploadedFile.value && !errorMessage.value
    }));

    function triggerFileInput() {
        if (props.disabled || isUploading.value) return;
        fileInput.value?.click();
    }

    function onDragOver(e) {
        if (props.disabled) return;
        isDragging.value = true;
    }

    function onDragLeave() {
        isDragging.value = false;
    }

    function onDrop(e) {
        isDragging.value = false;
        if (props.disabled) return;

        const files = e.dataTransfer.files;
        if (!files.length) return;

        if (props.maxFiles === 1) {
            processFile(files[0]);
        } else {
            const limited = Array.from(files).slice(0, props.maxFiles);
            limited.forEach(f => processFile(f, limited.length > 1));
        }
    }

    function onFileSelect(e) {
        const files = e.target.files;
        if (!files.length) return;

        if (props.maxFiles === 1) {
            processFile(files[0]);
        } else {
            const limited = Array.from(files).slice(0, props.maxFiles);
            limited.forEach(f => processFile(f, limited.length > 1));
        }
        e.target.value = '';
    }

    function processFile(file, isMultiple = false) {
        errorMessage.value = '';
        uploadedFile.value = null;

        // Validate file type
        if (props.acceptedFiles) {
            const accepted = props.acceptedFiles.split(',').map(t => t.trim());
            const fileType = file.type;
            if (!accepted.includes(fileType) && !accepted.includes('/*')) {
                const wildcard = accepted.find(a => a.endsWith('/*'));
                if (!wildcard || !fileType.startsWith(wildcard.split('/')[0])) {
                    errorMessage.value = `Invalid file type: ${fileType}. Accepted: ${props.acceptedFiles}`;
                    emit('error', file, { message: errorMessage.value });
                    return;
                }
            }
        }

        // Validate file size
        const maxBytes = props.maxFilesize * 1024 * 1024;
        if (file.size > maxBytes) {
            errorMessage.value = `File too large. Maximum ${props.maxFilesize}MB.`;
            emit('error', file, { message: errorMessage.value });
            return;
        }

        // Validate dimensions if specified
        if (props.height || props.width) {
            validateDimensions(file);
        } else {
            uploadFile(file);
        }
    }

    function validateDimensions(file) {
        const reader = new FileReader();
        reader.onload = e => {
            const img = new Image();
            img.onload = () => {
                if (props.width && img.width !== props.width) {
                    errorMessage.value = `Invalid width: ${img.width}px. Required: ${props.width}px.`;
                    emit('error', file, { message: errorMessage.value });
                    return;
                }
                if (props.height && img.height !== props.height) {
                    errorMessage.value = `Invalid height: ${img.height}px. Required: ${props.height}px.`;
                    emit('error', file, { message: errorMessage.value });
                    return;
                }
                uploadFile(file);
            };
            img.onerror = () => {
                errorMessage.value = 'Unable to read image dimensions.';
                emit('error', file, { message: errorMessage.value });
            };
            img.src = e.target.result;
        };
        reader.onerror = () => {
            errorMessage.value = 'Unable to read file.';
            emit('error', file, { message: errorMessage.value });
        };
        reader.readAsDataURL(file);
    }

    function uploadFile(file) {
        isUploading.value = true;
        uploadProgress.value = 0;

        const xhr = new XMLHttpRequest();
        xhr.open('POST', props.url);

        const hashTeatro = localStorage.getItem('hash_teatro');
        if (hashTeatro) {
            xhr.setRequestHeader('Hash-Teatro', hashTeatro);
        }
        xhr.setRequestHeader('Accept', 'application/json');

        xhr.upload.onprogress = e => {
            if (e.lengthComputable) {
                uploadProgress.value = (e.loaded / e.total) * 100;
            }
        };

        xhr.onload = () => {
            isUploading.value = false;
            if (xhr.status >= 200 && xhr.status < 300) {
                uploadedFile.value = file;
                let response;
                try {
                    response = JSON.parse(xhr.responseText);
                } catch (e) {
                    response = xhr.responseText;
                }
                // Push the file + server response into the multi-upload list.
                if (props.uploadMultiple) {
                    uploadedFilesLocal.value = [
                        ...uploadedFilesLocal.value,
                        { file, response }
                    ];
                    emit('update:uploadedFiles', uploadedFilesLocal.value);
                }
                // Refresh BEFORE emit so the parent listener and the
                // observer submit gate see the fresh provider value in
                // the same synchronous tick.
                refreshValidation();
                emit('success', file, response);
            } else {
                let response;
                try {
                    response = JSON.parse(xhr.responseText);
                } catch (e) {
                    response = xhr.responseText;
                }
                errorMessage.value = 'Upload failed.';
                emit('error', file, response);
            }
        };

        xhr.onerror = () => {
            isUploading.value = false;
            errorMessage.value = 'Network error. Upload failed.';
            emit('error', file, { message: 'Network error' });
        };

        const formData = new FormData();
        formData.append('file', file);
        xhr.send(formData);
    }

    function removeFile() {
        if (uploadedFile.value) {
            const file = uploadedFile.value;
            uploadedFile.value = null;
            errorMessage.value = '';
            uploadProgress.value = 0;
            // Refresh BEFORE emit so the parent listener and the observer
            // submit gate see the fresh provider value in the same tick.
            refreshValidation();
            emit('removed', file);
        }
    }

    /**
     * Remove a single file from the multi-upload list by index. Parents
     * bind this via `@remove-file="onRemoveFile"` so they can keep their
     * own ID arrays (e.g. documentosIds) in sync.
     */
    function removeUploadedFile(index) {
        if (index < 0 || index >= uploadedFilesLocal.value.length) return;
        const removed = uploadedFilesLocal.value[index];
        uploadedFilesLocal.value = uploadedFilesLocal.value.filter((_, i) => i !== index);
        emit('update:uploadedFiles', uploadedFilesLocal.value);
        emit('remove-file', { index, entry: removed });
    }

    function resetDropzone() {
        errorMessage.value = '';
        uploadedFile.value = null;
        uploadProgress.value = 0;
        isUploading.value = false;
    }

    defineExpose({
        removeUploadedFile,
        uploadedFiles: uploadedFilesLocal
    });
</script>